import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Activity } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const ACTION_LABELS_FR = {
  "user.create": "Création utilisateur",
  "user.update": "Modification utilisateur",
  "user.delete": "Suppression utilisateur",
  "settings.update": "Modification des paramètres",
  "subscription.cancel": "Annulation souscription",
  "email_template.update": "Modification template email",
  "reminder.send": "Envoi email de relance",
};
const ACTION_LABELS_EN = {
  "user.create": "User created",
  "user.update": "User updated",
  "user.delete": "User deleted",
  "settings.update": "Settings updated",
  "subscription.cancel": "Subscription cancelled",
  "email_template.update": "Email template updated",
  "reminder.send": "Reminder sent",
};

const AuditLogs = ({ tAdmin, lang }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const labels = lang === "en" ? ACTION_LABELS_EN : ACTION_LABELS_FR;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/audit-logs", { params: { limit: 200 } });
      setLogs(res.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []);

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6" data-testid="audit-logs">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#0A192F] flex items-center gap-2">
            <Activity size={20} className="text-[#C5A059]" /> {tAdmin.audit.title}
          </h2>
          <p className="text-sm text-[#64748B] mt-1">{tAdmin.audit.subtitle}</p>
        </div>
        <button onClick={fetchLogs} className="p-2 rounded-md hover:bg-black/5 text-[#64748B]" data-testid="audit-refresh-btn">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-[#64748B]">…</div>
      ) : logs.length === 0 ? (
        <div className="p-12 text-center text-[#64748B]" data-testid="audit-empty">{tAdmin.audit.empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] border-b border-black/5">
              <tr className="text-left text-[#64748B] uppercase text-[11px] tracking-wider">
                <th className="px-4 py-3 font-medium">{tAdmin.audit.colWhen}</th>
                <th className="px-4 py-3 font-medium">{tAdmin.audit.colActor}</th>
                <th className="px-4 py-3 font-medium">{tAdmin.audit.colAction}</th>
                <th className="px-4 py-3 font-medium">{tAdmin.audit.colTarget}</th>
                <th className="px-4 py-3 font-medium">{tAdmin.audit.colDetails}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={l.id} className="border-b border-black/5 last:border-0 hover:bg-[#FDFBF7]" data-testid={`audit-row-${i}`}>
                  <td className="px-4 py-3 text-[#64748B] whitespace-nowrap text-xs">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#0A192F]">{l.actor_email}</div>
                    {l.actor_role && <div className="text-[10px] uppercase text-[#64748B]">{l.actor_role}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded text-[11px] font-mono bg-[#FDF8EE] text-[#7A6028]">
                      {labels[l.action] || l.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#334155] font-mono text-xs">{l.target || "—"}</td>
                  <td className="px-4 py-3 text-[11px] text-[#64748B]">
                    {l.meta && Object.keys(l.meta).length > 0 ? (
                      <code className="bg-[#F3F0EA] px-2 py-1 rounded">{JSON.stringify(l.meta)}</code>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
